import React from "react";
import { useParams } from "react-router-dom";
import useApiEp from "../hooks/useApiEp";
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CircularProgress,
  Divider,
  Grid,
  Chip,
} from "@mui/material";
import {
  Percent as PercentIcon,
  Scale as ScaleIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import BackBtn from "../widgets/BackBtn";

const ViewProduct = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error } = useApiEp(`admin/tool/${id}`);

  if (isLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );

  if (isError)
    return (
      <Box textAlign="center" color="error.main" mt={4}>
        <Typography variant="h6">Failed to load product</Typography>
        <Typography variant="body2">
          {error?.message || "Unknown error"}
        </Typography>
      </Box>
    );

  if (!data)
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6">No product found</Typography>
      </Box>
    );

  const {
    name,
    description,
    mrp,
    sellingPrice,
    discount,
    category,
    stock,
    gst,
    weight,
    weightUnit,
    images = [],
    createdOn,
    updatedOn,
  } = data;

  // Helper: format price with rupee symbol
  const formatPrice = (price) => `₹${parseFloat(price || 0).toFixed(2)}`;

  // Helper: format weight with unit
  const formatWeight = (weight, unit) => {
    if (!weight) return null;
    const weightValue = parseFloat(weight);
    const unitDisplay = unit || "kg";
    return `${weightValue} ${unitDisplay}`;
  };

  // Calculate discount if not stored
  const calculateDiscount = () => {
    if (!mrp || !sellingPrice || parseFloat(mrp) <= 0) return null;
    const disc =
      ((parseFloat(mrp) - parseFloat(sellingPrice)) / parseFloat(mrp)) * 100;
    return disc > 0 ? disc.toFixed(1) : null;
  };

  const storedDiscount = discount != null ? parseFloat(discount) : null;
  const displayDiscount = storedDiscount ?? calculateDiscount();

  return (
    <Box p={3}>
      <BackBtn />
      <Card sx={{ maxWidth: 1000, mx: "auto", borderRadius: 3, boxShadow: 3 }}>
        {/* Images Section */}
        {images.length === 0 ? (
          <Box
            sx={{
              height: 300,
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">No images available</Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ p: 2 }}>
            {images.map((imgUrl, index) => (
              <Grid item xs={12} md={6} key={index}>
                <CardMedia
                  component="img"
                  sx={{ height: 250, borderRadius: 2 }}
                  image={imgUrl}
                  alt={`${name} image ${index + 1}`}
                />
              </Grid>
            ))}
          </Grid>
        )}
        <CardContent>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {name}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            {description}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Category
              </Typography>
              <Typography variant="body1">{category}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Stock
              </Typography>
              <Typography variant="body1">{stock}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                MRP
              </Typography>
              <Typography variant="body1">{formatPrice(mrp)}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Selling Price
              </Typography>
              <Typography
                variant="body1"
                color="success.main"
                fontWeight={600}
                sx={{ fontSize: "1.1rem" }}
              >
                {formatPrice(sellingPrice)}
              </Typography>
            </Grid>

            {/* GST Row */}
            {gst != null && parseFloat(gst) > 0 && (
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  GST
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <ReceiptIcon sx={{ fontSize: 18, color: "info.main" }} />
                  <Typography variant="body1" fontWeight={500}>
                    {parseFloat(gst).toFixed(2)}%
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Weight Row */}
            {weight != null && parseFloat(weight) > 0 && (
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Weight
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <ScaleIcon sx={{ fontSize: 18, color: "secondary.main" }} />
                  <Typography variant="body1" fontWeight={500}>
                    {formatWeight(weight, weightUnit)}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Discount Row */}
            {displayDiscount != null && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Offer
                </Typography>
                <Chip
                  icon={<PercentIcon sx={{ fontSize: 16 }} />}
                  label={`${displayDiscount}% OFF`}
                  color="error"
                  size="small"
                  sx={{
                    bgcolor: "error.light",
                    color: "error.contrastText",
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="caption" color="text.secondary">
            Created: {new Date(createdOn).toLocaleString()}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Last Updated: {new Date(updatedOn).toLocaleString()}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ViewProduct;
